import {
  useId,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
} from 'react';
import { Link, useNavigate } from 'react-router';

import { errorMessage } from '../lib/api';
import { ChevronDownIcon, ChevronLeftIcon } from './icons';

export function Card({
  children,
  className = '',
  testId,
  onClick,
}: {
  children: ReactNode;
  className?: string;
  testId?: string;
  onClick?: () => void;
}) {
  return (
    <section
      className={`rounded-2xl bg-white p-4 shadow-sm border border-slate-100/80 ${className}`}
      data-testid={testId}
      onClick={onClick}
    >
      {children}
    </section>
  );
}

export function PageTitle({
  children,
  back,
  subtitle,
  action,
  crumb,
  desc,
}: {
  children: ReactNode;
  back?: string | boolean | (() => void);
  subtitle?: string;
  /** Desktop (Member Area): breadcrumb "Access / …" di atas judul, tanpa tombol kembali. */
  crumb?: string;
  /** Desktop: keterangan di bawah judul. */
  desc?: string;
  /** Tombol kanan di app bar (mis. ikon info), hanya untuk layar dengan tombol kembali. */
  action?: ReactNode;
}) {
  const navigate = useNavigate();

  const handleBack = () => {
    if (typeof back === 'function') {
      back();
    } else if (typeof back === 'string') {
      // If user has SPA history, pop back; otherwise fallback to the provided URL
      const historyState = window.history.state as { idx?: unknown } | null;
      if (typeof historyState?.idx === 'number' && historyState.idx > 0) {
        void navigate(-1);
      } else {
        void navigate(back);
      }
    } else {
      void navigate(-1);
    }
  };

  // Layar turunan (Figma): app bar dengan tombol kembali di kiri dan judul di tengah (mobile);
  // desktop: breadcrumb + judul besar rata kiri, atau tombol kembali bulat + judul.
  if (back) {
    return (
      <div className="mb-4 md:mb-6">
        <div className="hidden md:block">
          {crumb ? (
            <>
              <p className="text-[11px] text-slate-500">{crumb}</p>
              <h1 className="mt-1 text-[28px] leading-tight font-bold text-fola-ink">{children}</h1>
            </>
          ) : (
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={handleBack}
                className="flex size-9 cursor-pointer items-center justify-center rounded-full border border-slate-200 bg-white text-fola-ink transition hover:bg-slate-50"
                aria-label="Kembali"
              >
                <ChevronLeftIcon className="size-5" strokeWidth={2} />
              </button>
              <h1 className="flex-1 text-2xl font-bold text-fola-ink">{children}</h1>
              {action}
            </div>
          )}
          {desc ? <p className="mt-1.5 text-sm text-slate-500">{desc}</p> : null}
        </div>
        <div className="sticky top-0 z-20 -mx-4 grid h-14 grid-cols-[2.75rem_1fr_2.75rem] items-center bg-white/95 px-2 backdrop-blur-md sm:-mx-6 sm:px-4 md:hidden">
          <button
            type="button"
            onClick={handleBack}
            className="flex size-10 cursor-pointer items-center justify-center rounded-full text-fola-ink transition select-none hover:bg-slate-100 active:scale-95"
            aria-label="Kembali"
          >
            <ChevronLeftIcon className="size-6" strokeWidth={2} />
          </button>
          <h1 className="truncate text-center text-base font-bold text-fola-ink">{children}</h1>
          <div className="flex justify-end">{action}</div>
        </div>
        {subtitle ? <p className="mt-1 text-xs text-slate-500">{subtitle}</p> : null}
      </div>
    );
  }

  return (
    <div className="mb-4">
      <h1 className="text-2xl font-bold tracking-tight text-fola-ink">{children}</h1>
      {subtitle ? <p className="mt-1 text-xs text-slate-500">{subtitle}</p> : null}
    </div>
  );
}

type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost' | 'gradient';
const VARIANT: Record<ButtonVariant, string> = {
  primary:
    'bg-fola-gradient-x text-white hover:opacity-95 disabled:bg-none disabled:bg-slate-200 disabled:text-slate-400',
  gradient:
    'bg-fola-gradient-x text-white hover:opacity-95 disabled:bg-none disabled:bg-slate-200 disabled:text-slate-400',
  secondary: 'bg-white text-fola-ink border border-slate-200 hover:bg-slate-50 disabled:opacity-50',
  danger: 'bg-[#E43437] text-white hover:bg-red-700 disabled:opacity-50 shadow-sm',
  ghost: 'text-slate-700 hover:bg-slate-100 disabled:opacity-50',
};

export function Button({
  variant = 'primary',
  loading = false,
  className = '',
  children,
  disabled,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: ButtonVariant; loading?: boolean }) {
  return (
    <button
      type="button"
      className={`inline-flex min-h-12 cursor-pointer items-center justify-center gap-2 rounded-full px-6 text-sm font-bold transition select-none active:scale-[0.99] disabled:cursor-not-allowed ${VARIANT[variant]} ${className}`}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? (
        <span
          className="size-4 animate-spin rounded-full border-2 border-current border-t-transparent"
          aria-hidden
        />
      ) : null}
      {children}
    </button>
  );
}

export function ButtonLink({
  to,
  children,
  variant = 'primary',
  className = '',
}: {
  to: string;
  children: ReactNode;
  variant?: ButtonVariant;
  className?: string;
}) {
  return (
    <Link
      to={to}
      className={`inline-flex min-h-12 items-center justify-center gap-2 rounded-full px-6 text-sm font-bold transition select-none active:scale-[0.99] ${VARIANT[variant]} ${className}`}
    >
      {children}
    </Link>
  );
}

/** Gaya label & input Figma (registrasi, login, pembayaran). */
export const LABEL_CLASS = 'block text-[13px] font-medium text-slate-800';
export const INPUT_CLASS =
  'block min-h-12 w-full rounded-xl border bg-white px-4 text-base text-fola-ink outline-none transition placeholder:text-slate-400 focus:border-fola-indigo focus:ring-2 focus:ring-fola-indigo/20';

export function Field({
  label,
  error,
  hint,
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & { label: string; error?: string | undefined; hint?: string }) {
  const id = useId();
  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className={LABEL_CLASS}>
        {label}
      </label>
      <input
        id={id}
        className={`${INPUT_CLASS} ${error ? 'border-red-500 ring-1 ring-red-500/20' : 'border-slate-200'}`}
        aria-invalid={error ? true : undefined}
        aria-describedby={error || hint ? `${id}-desc` : undefined}
        {...rest}
      />
      {error ? (
        <p id={`${id}-desc`} className="text-xs font-medium text-red-600">
          {error}
        </p>
      ) : hint ? (
        <p id={`${id}-desc`} className="text-xs text-slate-500">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

export function Select({
  label,
  children,
  error,
  ...rest
}: SelectHTMLAttributes<HTMLSelectElement> & { label: string; children: ReactNode; error?: string }) {
  const id = useId();
  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className={LABEL_CLASS}>
        {label}
      </label>
      <div className="relative">
        <select
          id={id}
          className={`${INPUT_CLASS} appearance-none pr-10 invalid:text-slate-400 ${error ? 'border-red-500 ring-1 ring-red-500/20' : 'border-slate-200'}`}
          {...rest}
        >
          {children}
        </select>
        <ChevronDownIcon className="pointer-events-none absolute top-1/2 right-3.5 size-4 -translate-y-1/2 text-slate-500" />
      </div>
      {error ? <p className="text-xs font-medium text-red-600">{error}</p> : null}
    </div>
  );
}

export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
}: {
  options: { label: string; value: T }[];
  value: T;
  onChange: (val: T) => void;
}) {
  return (
    <div className="flex rounded-xl bg-slate-100 p-1">
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          aria-pressed={value === opt.value}
          className={`flex-1 min-h-10 rounded-lg text-[13px] transition select-none ${
            value === opt.value
              ? 'bg-white font-bold text-fola-ink shadow-sm'
              : 'font-medium text-slate-500 hover:text-slate-800'
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

export function Loading({ label = 'Memuat…' }: { label?: string }) {
  return (
    <div role="status" className="flex flex-col items-center justify-center gap-3 py-12 text-slate-600">
      <div className="size-8 animate-spin rounded-full border-3 border-fola-indigo border-t-transparent" />
      <span className="text-xs font-medium text-slate-500">{label}</span>
    </div>
  );
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  return (
    <div role="alert" className="rounded-2xl bg-red-50 p-4 text-sm text-red-800 border border-red-200">
      <p>{errorMessage(error)}</p>
      {onRetry ? (
        <Button variant="danger" className="mt-3 text-xs min-h-9" onClick={onRetry}>
          Coba lagi
        </Button>
      ) : null}
    </div>
  );
}

export function Empty({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="rounded-2xl border border-dashed border-slate-200 p-8 text-center bg-slate-50/50">
      <p className="font-semibold text-slate-800">{title}</p>
      {children ? <div className="mt-1 text-xs text-slate-500">{children}</div> : null}
    </div>
  );
}

export function Alert({
  tone = 'info',
  children,
}: {
  tone?: 'info' | 'success' | 'error' | 'warning';
  children: ReactNode;
}) {
  const tones = {
    info: 'bg-blue-50 text-blue-900 border-blue-200',
    success: 'bg-emerald-50 text-emerald-900 border-emerald-200',
    error: 'bg-red-50 text-red-800 border-red-200',
    warning: 'bg-amber-50 text-amber-900 border-amber-200',
  };
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={`rounded-xl p-3.5 text-xs font-medium border ${tones[tone]}`}
    >
      {children}
    </div>
  );
}

const BADGE_TONE: Record<string, string> = {
  green: 'bg-emerald-100 text-emerald-900',
  amber: 'bg-amber-100 text-amber-900',
  red: 'bg-red-100 text-red-800',
  slate: 'bg-slate-100 text-slate-800',
  sky: 'bg-sky-100 text-sky-900',
  purple: 'bg-purple-100 text-purple-900',
};

const STATUS_TONE: Record<string, keyof typeof BADGE_TONE> = {
  COMPLETED: 'green',
  RELEASED: 'green',
  ACTIVE: 'green',
  PAID: 'green',
  SUCCESS: 'green',
  APPROVED: 'green',
  VERIFIED: 'green',
  REINSTATED: 'sky',
  PENDING: 'amber',
  PENDING_PAYMENT: 'amber',
  PENDING_INQUIRY: 'amber',
  REQUESTED: 'amber',
  PROCESSING: 'amber',
  CANCELLED: 'red',
  RETURNED: 'red',
  VOID: 'red',
  EXPIRED: 'red',
  FAILED: 'red',
  REJECTED: 'red',
  SUSPENDED: 'red',
};

export function StatusBadge({ status, label }: { status: string; label: string }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-bold ${BADGE_TONE[STATUS_TONE[status] ?? 'slate']}`}
    >
      {label}
    </span>
  );
}

export function Row({
  label,
  children,
  strong = false,
}: {
  label: ReactNode;
  children: ReactNode;
  strong?: boolean;
}) {
  return (
    <div
      className={`flex items-start justify-between gap-3 py-1.5 text-sm ${strong ? 'font-semibold text-slate-900' : 'text-slate-600'}`}
    >
      <dt>{label}</dt>
      <dd className="text-right tabular-nums">{children}</dd>
    </div>
  );
}
